import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useSpring, useTransform } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";

// Ordered chapters — matches the flow of the page.
const SECTIONS = [
  "philosophy",
  "rituals",
  "services",
  "offers",
  "gifts",
  "team",
  "reviews",
  "book",
  "visit",
];

// Localized labels — `rituals` isn't in the nav so provide fallback here.
const LABELS = {
  en: {
    philosophy: "Philosophy",
    rituals: "Rituals",
    services: "Services",
    offers: "Offers",
    gifts: "Gifts",
    team: "Practice",
    reviews: "Reviews",
    book: "Book",
    visit: "Visit",
  },
  zh: {
    philosophy: "理念",
    rituals: "礼",
    services: "项目",
    offers: "优惠",
    gifts: "礼卡",
    team: "团队",
    reviews: "评价",
    book: "预约",
    visit: "到访",
  },
  es: {
    philosophy: "Filosofía",
    rituals: "Rituales",
    services: "Servicios",
    offers: "Ofertas",
    gifts: "Regalos",
    team: "Equipo",
    reviews: "Reseñas",
    book: "Reservar",
    visit: "Visita",
  },
};

export const ScrollProgress = () => {
  const { lang } = useLang();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 140, damping: 24, mass: 0.5 });
  // Position of the leading "ember" — follows the fill's right edge
  const emberLeft = useTransform(scaleX, [0, 1], ["0%", "100%"]);
  const [positions, setPositions] = useState({});
  const [active, setActive] = useState(null);
  const [pastHero, setPastHero] = useState(false);
  const labels = LABELS[lang] || LABELS.en;

  // Compute each section's position along the scroll timeline.
  useEffect(() => {
    const compute = () => {
      const next = {};
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      for (const id of SECTIONS) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top + window.scrollY;
        next[id] = Math.min(100, Math.max(0, (top / scrollable) * 100));
      }
      setPositions(next);
    };
    compute();
    window.addEventListener("resize", compute);
    // Recompute a couple times after mount — videos/images shift layout as they load
    const t1 = setTimeout(compute, 1500);
    const t2 = setTimeout(compute, 4000);
    return () => {
      window.removeEventListener("resize", compute);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  // Which section is currently in view (highest intersection ratio in center band).
  useEffect(() => {
    const ratios = {};
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) ratios[e.target.id] = e.intersectionRatio;
        let best = null;
        let bestR = 0;
        for (const id of SECTIONS) {
          const r = ratios[id] || 0;
          if (r > bestR) {
            bestR = r;
            best = id;
          }
        }
        if (best) setActive(best);
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    for (const id of SECTIONS) {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    }
    return () => io.disconnect();
  }, []);

  // Hide the pill while the guest is still on the hero (feels less cluttered on landing).
  useEffect(() => {
    const onScroll = () => setPastHero(window.scrollY > window.innerHeight * 0.6);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const activeIndex = SECTIONS.findIndex((s) => s === active);

  return (
    <>
      {/* CHAPTER REEL — molten fire bar with a live-flame ember at the leading edge.
          Positioned at top-8 (below the gold announcement bar) so it reads against the dark navbar. */}
      <div className="fixed top-8 left-0 right-0 h-[5px] z-[110] pointer-events-none" data-testid="chapter-reel">
        {/* Dark rail for contrast */}
        <span className="absolute inset-0 bg-[#050706]/85" />

        {/* Filled molten line — white-hot core, amber outer, subtle animated shimmer */}
        <motion.span
          data-testid="scroll-progress"
          style={{ scaleX }}
          className="molten-bar block absolute inset-0 origin-left"
        />

        {/* Leading-edge ember — pulses like a candle flame */}
        <motion.div
          aria-hidden="true"
          style={{ left: emberLeft }}
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 pointer-events-none"
        >
          <motion.span
            animate={{ scale: [1, 1.35, 1], opacity: [0.85, 1, 0.85] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            className="block w-3 h-3 rounded-full bg-[#fff2cc] shadow-[0_0_18px_6px_rgba(255,214,120,0.75),0_0_38px_14px_rgba(232,120,20,0.35)]"
          />
        </motion.div>
        {/* Tick marks — one per section */}
        {SECTIONS.map((id) => {
          const p = positions[id];
          if (p == null) return null;
          const isActive = active === id;
          return (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              style={{ left: `${p}%` }}
              data-testid={`chapter-tick-${id}`}
              aria-label={`Jump to ${labels[id] || id}`}
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 pointer-events-auto group h-6 flex items-center justify-center"
            >
              <span
                className={`block w-[3px] transition-all duration-300 ${
                  isActive
                    ? "h-5 bg-[#dcc286] shadow-[0_0_12px_rgba(220,194,134,0.9)]"
                    : "h-2.5 bg-[#c5a059]/50 group-hover:h-4 group-hover:bg-[#dcc286]"
                }`}
              />
              <span className="absolute top-7 left-1/2 -translate-x-1/2 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300 text-[9px] tracking-[0.28em] uppercase text-[#dcc286] bg-[#050706]/90 border border-[#c5a059]/25 px-2 py-1 backdrop-blur-sm">
                {labels[id] || id}
              </span>
            </button>
          );
        })}
      </div>

      {/* MORPHING SECTION PILL — bottom-right, appears past the hero */}
      <AnimatePresence>
        {pastHero && active && labels[active] && (
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 12, x: 4 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            exit={{ opacity: 0, y: -8, x: 4, transition: { duration: 0.35 } }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            data-testid="section-pill"
            className="fixed bottom-8 right-6 z-[105] pointer-events-none flex items-center gap-3 bg-[#050706]/75 backdrop-blur-md border border-[#c5a059]/25 px-4 py-2.5 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.6)]"
          >
            <span className="font-serif-display italic text-[#c5a059] text-lg leading-none">
              0{activeIndex + 1}
            </span>
            <span className="w-px h-4 bg-[#c5a059]/30" />
            <span className="text-[10px] tracking-[0.35em] uppercase text-[#f0f4f2]/85">
              {labels[active]}
            </span>
            <span className="text-[10px] font-serif-display italic text-[#c5a059]/40">
              /{String(SECTIONS.length).padStart(2, "0")}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
